import React from 'react';
import { Link } from 'react-router-dom';
import './Services.scss';

interface Service {
  id: number;
  title: string;
  description: string;
  icon: string;
  features: string[];
  price?: string;
  route?: string;
}

const Services: React.FC = () => {
  const services: Service[] = [
    {
      id: 1,
      title: 'Comprehensive Psychiatric Evaluation',
      description: 'Thorough, evidence-based evaluations designed to understand your mental health concerns from every angle. We assess psychiatric symptoms, medical history, lifestyle factors, and emotional stressors to build a complete picture of your well-being. This detailed evaluation forms the foundation of your personalized treatment plan and ensures every recommendation is informed, accurate, and clinically sound. Our comprehensive approach includes a thorough review of your current symptoms, past treatment experiences, family history, and any co-occurring medical conditions that may impact your mental health.',
      icon: '🔍',
      features: [
        'Complete psychiatric symptom assessment covering mood, anxiety, thought processes, and behavior',
        'Comprehensive medical history review including medications, allergies, and past treatments',
        'Lifestyle and emotional stressor evaluation to identify contributing factors',
        'Family history assessment to understand genetic and environmental influences',
        'Personalized treatment plan development tailored to your unique needs',
        'Clinically sound recommendations based on evidence-based practices',
        'Collaborative approach ensuring your voice is heard in treatment decisions'
      ]
    },
    {
      id: 2,
      title: 'Supportive psychotherapy',
      description: 'Integrated treatment plans combining expert medication management with supportive, therapeutic guidance. We closely monitor your response to medications and adjust as needed to ensure safety, effectiveness, and long-term stability. Combined with therapy-informed techniques, this approach helps manage depression, anxiety, bipolar disorder, PTSD, ADHD, and other conditions with clarity and confidence. Our medication management includes careful selection of appropriate medications, dosage optimization, side effect monitoring, and regular follow-ups to ensure optimal outcomes. We work collaboratively with you to find the right medication regimen that balances effectiveness with minimal side effects.',
      icon: '💊',
      features: [
        'Expert medication prescribing and monitoring by board-certified psychiatrists',
        'Safety and effectiveness tracking with regular follow-up appointments',
        'Therapy-informed techniques integrated into medication management',
        'Treatment for depression, anxiety, bipolar disorder, PTSD, ADHD, and other conditions',
        'Long-term stability support with ongoing medication adjustments as needed',
        'Comprehensive side effect management and monitoring',
        'Coordination with other healthcare providers when necessary',
        'Patient education about medications, expected outcomes, and potential side effects'
      ]
    },
    {
      id: 3,
      title: 'Transcranial Magnetic Stimulation (TMS) & Innovative Therapies',
      description: 'Cutting-edge brain-based treatments for patients who have not achieved results with traditional approaches. TMS is a non-invasive, FDA-approved therapy that uses gentle magnetic pulses to stimulate areas of the brain involved in mood regulation. We provide comprehensive evaluation, customized TMS protocols, and consistent monitoring to ensure the best possible outcomes for treatment-resistant depression and other complex conditions. TMS therapy is performed in our office, typically requiring daily sessions over several weeks. The procedure is well-tolerated, requires no anesthesia, and allows you to return to your normal activities immediately after each session.',
      icon: '🧠',
      features: [
        'Non-invasive, FDA-approved TMS therapy with no systemic side effects',
        'Customized TMS protocols tailored to your specific needs and response',
        'Treatment-resistant depression care for patients who haven\'t responded to medications',
        'Comprehensive evaluation and monitoring throughout the treatment course',
        'Advanced brain-based treatment options using the latest technology',
        'Well-tolerated procedure with minimal discomfort',
        'No anesthesia required - you remain awake and alert during treatment',
        'Typically 36 sessions over 6-9 weeks, with sessions lasting about 20-40 minutes',
        'Insurance coverage available for qualifying conditions'
      ]
    },
    {
      id: 4,
      title: 'Telehealth & Flexible Care Options',
      description: 'Secure, convenient virtual visits designed to fit your schedule while maintaining the highest standard of psychiatric care. Whether at home, work, or traveling, patients can access medication management, follow-ups, and mental health support through our HIPAA-compliant telehealth platform. In-office visits remain available for those who prefer an in-person experience, ensuring flexibility without compromising care quality. Our telehealth services provide the same level of comprehensive care as in-person visits, with the added convenience of accessing care from anywhere. We understand that life can be busy, and we\'re committed to making quality mental health care accessible and convenient.',
      icon: '💻',
      features: [
        'HIPAA-compliant telehealth platform ensuring your privacy and security',
        'Virtual medication management and prescription services',
        'Remote follow-up appointments for ongoing care',
        'Flexible scheduling options to accommodate your lifestyle',
        'In-office visits available for those who prefer face-to-face interaction',
        'Same quality of care whether virtual or in-person',
        'Easy-to-use video conferencing technology',
        'Access to care from home, work, or while traveling',
        'Reduced travel time and increased convenience',
        'Continuity of care even when you\'re away from the area'
      ]
    }
  ];

  return (
    <div className="services-container">
      <div className="services-header">
        <h1>Our Services</h1>
        <p>Compassionate, evidence-based mental health care tailored to your unique needs</p>
      </div>

      <div className="services-sections">
        {services.map((service, i) => (
          <section
            key={service.id}
            className={`service-section ${i % 2 === 1 ? 'alternate' : ''}`}
          >
            <div className="service-content">
              <div className="service-icon-wrapper">
                <div className="service-icon">{service.icon}</div>
              </div>
              <div className="service-text">
                <h2>{service.title}</h2>
                <p className="service-description">{service.description}</p>
                <ul className="features-list">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <div className="service-actions">
                  <Link to="/contact" className="contact-btn">Contact Us</Link>
                </div>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default Services;

